import { useTheme } from '@mui/material/styles';
import type { FormikHelpers } from 'formik';
import { FieldArray, Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import * as Yup from 'yup';

import DrilldownItem from '@components/DrilldownItem';
import Dropdown from '@components/Dropdown';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';

import {
  ALL_OBSERVATION_OPTIONS_MAP,
  BODY_OBSERVATION_CODE_OPTIONS,
  BODY_OBSERVATION_OPTIONS_MAP,
  MANUAL_ENTRY_FITNESS_CODE_OPTIONS,
  MANUAL_ENTRY_FITNESS_OPTIONS_MAP,
  VITAL_SIGN_CODE_OPTIONS,
  VITAL_SIGN_OPTIONS_MAP,
} from '@constants/option-types/vital-signs';
import { BODY, FITNESS, VITAL_SIGNS } from '@constants/routes';

import {
  useCreateBodyObservation,
  useGetBodyObservation,
  useUpdateBodyObservation,
} from '@hooks/api/body-observation';
import { useCreateFitnessObservation } from '@hooks/api/fitness-observation';
import {
  useCreateVitalSign,
  useGetVitalSign,
  useUpdateVitalSign,
} from '@hooks/api/vital-sign';
import { useCuration } from '@hooks/useCuration';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout } from '@layouts/DrilldownStyle';

import type { FhirResource } from '@models/fhir-resource';
import { getVitalSignTemplate } from '@models/fhir-templates/observation';
import type { ReducedObservationComponent } from '@models/observation';
import type { FormValues, VitalSignValues } from '@models/vital-sign';
import { TrackerType } from '@models/vital-sign';
import {
  HEALTH_TRACKER_MAP,
  VitalSignObservationRecord,
} from '@models/vital-sign';

import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';

import type { Annotation, ResourceAssociationUnion } from 'generated/types';

import DrilldownWrapper from '../Common/DrilldownWrapper';

const messages = defineMessages({
  vitalSign: {
    id: 'vitalSignForm.vitalSign',
    defaultMessage: 'Vital Sign',
  },
  body: {
    id: 'vitalSignForm.body',
    defaultMessage: 'Body',
  },
  dailyStats: {
    id: 'vitalSignForm.dailyStats',
    defaultMessage: 'Daily Stats',
  },
  practitioner: {
    id: 'GLOBAL.practitioner',
    defaultMessage: 'Practitioner',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  number: {
    id: 'vitalSignForm.number',
    defaultMessage: 'This field must be a number',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  unit: {
    id: 'vitalSignForm.unit',
    defaultMessage: 'Unit',
  },
  value: {
    id: 'vitalSignForm.value',
    defaultMessage: 'Value',
  },
  clinicalStatus: {
    id: 'vitalSigns.clinicalStatus',
    defaultMessage: 'Clinical Status',
  },
  clinical: {
    id: 'vitalSigns.clinical',
    defaultMessage: 'Clinical',
  },
  nonClinical: {
    id: 'vitalSigns.nonClinical',
    defaultMessage: 'Non-Clinical',
  },
  testDate: {
    id: 'vitalSignForm.testDate',
    defaultMessage: 'Date',
  },
  type: {
    id: 'GLOBAL.type',
    defaultMessage: 'Type',
  },
  systolic: {
    id: 'vitalSignForm.systolic',
    defaultMessage: 'Systolic',
  },
  diastolic: {
    id: 'vitalSignForm.diastolic',
    defaultMessage: 'Diastolic',
  },
  notFound: {
    id: 'GLOBAL.unfoundResource',
    defaultMessage: 'Resource not found',
  },
});

/**
 * Auto-curation's plain-object record: a bag of OCR'd fields, plus the few stub
 * methods ResourceFormWrapper attaches to every curation record. It is NOT a
 * hydrated FHIR record, so the accessors that read the underlying FHIR model
 * (getComponents, getTestDateStr) are absent and typed `undefined` here.
 */
type VitalSignCurationData = Partial<VitalSignValues> & {
  id?: string | null;
  unit?: string | null;
  displayName?: string;
  components?: ReducedObservationComponent[];
  associatedRecords?: ResourceAssociationUnion[];
  recordNotes?: Annotation[];

  /* Stubs ResourceFormWrapper adds to every curation record. */
  getId?: () => string | null;
  getResourceId?: () => string | null;
  isShareable?: () => boolean;
  getDisplayName?: () => string;
  /**
   * ResourceFormWrapper attaches this to *every* record, so its presence says
   * nothing about the record's shape. When the wrapped record has no
   * getModelInput of its own — i.e. exactly the auto-curation case — the stub
   * echoes the raw FormValues straight back, which is not an
   * ObservationModelInput (FormValues carries submit/files/toPublish/components).
   * Typed as `unknown` so it cannot reach the mutation without a hydrated
   * record; build the input from getVitalSignTemplate instead.
   */
  getModelInput?: (values: FormValues) => unknown;

  /* Hydrated-record accessors curation data never has. */
  getComponents?: undefined;
  getTestDateStr?: undefined;
};

/**
 * Auto-curation routes every detected Observation to this form under the single
 * 'Vital Sign' key, but discern's vital worker emits body codes under that same
 * title: `vital_observation_model.Vital_signs`
 * (discern-modelling/AutoCuration/sagemaker/code/resource_models.py) permits
 * body-height, body-weight, blood-sugar and body-mass-index alongside the six
 * true vital-sign codes. Those four live in BODY_OBSERVATION_OPTIONS_MAP, so
 * leaving them on the Vital Signs tracker leaves the Type dropdown with no
 * matching option, renders the header as "-", and would file them under the
 * wrong observation category. Route them to the Body tracker this form already
 * implements.
 */
function resolveCurationTrackerType(code?: string | null): TrackerType {
  return code && code in BODY_OBSERVATION_OPTIONS_MAP
    ? TrackerType.Body
    : TrackerType.VitalSigns;
}

/**
 * Both VitalSignObservationRecord.getMeasurementArray and getVitalSignTemplate
 * index ALL_OBSERVATION_OPTIONS_MAP directly and neither checks the key exists.
 * Curation feeds this form raw OCR codes, which can be a code discern knows but
 * Engage has no option for (discern emits `waist-circumference`, which is in no
 * Engage option map) or, if the model ever escapes its enum, free text such as
 * 'Body Weight'. Either one throws "Cannot read properties of undefined
 * (reading 'components')" during render.
 */
function isSupportedObservationCode(code?: string | null): boolean {
  return Boolean(
    code &&
    VitalSignObservationRecord.getVitallCode(code) in
      ALL_OBSERVATION_OPTIONS_MAP
  );
}

/** The curation-side aliases this form answers to, on top of TrackerType. */
type CurationFormType = 'vital-sign' | 'Vital Sign' | 'Body';

/**
 * Curation passes the FormType key straight through as `type` (see
 * ResourceFormWrapper), so this receives display strings rather than
 * TrackerTypes. 'Body' in particular is not TrackerType.Body — that is the
 * lowercase 'body' — and an unmapped value reaches HEALTH_TRACKER_MAP[type],
 * whose destructure of an undefined entry throws before the form can render.
 */
function normalizeTrackerType(
  rawType: TrackerType | CurationFormType,
  curationCode?: string | null
): TrackerType {
  if (rawType === 'Body') {
    return TrackerType.Body;
  }
  if (rawType === 'vital-sign' || rawType === 'Vital Sign') {
    return resolveCurationTrackerType(curationCode);
  }
  return rawType as TrackerType;
}

interface VitalSignFormDrilldownProps {
  id?: string;
  type: TrackerType | CurationFormType;
  /**
   * When true, suppresses the default redirect to the VITAL_SIGNS or BODY route
   * after successfully creating a new vital sign or body observation.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
  /**
   * Callback invoked after successful form submission.
   * Used by the curation flow to remove the form from the list.
   */
  onSubmit?: () => void;
  /**
   * Auto-curation's in-memory record, prefilled from the OCR'd resource.
   * An auto-curated resource has no FHIR id yet, so the id-based fetch below
   * is skipped and this takes precedence — same pattern as SingleLabDrilldown.
   * On the curation path this is a plain object without hydrated record
   * methods (e.g. getTestDateStr, getComponents) — do not assume otherwise.
   */
  data?: VitalSignObservationRecord | VitalSignCurationData;
}

const VitalSignFormDrilldown: React.FC<VitalSignFormDrilldownProps> = ({
  id = '',
  type: rawType,
  disableNavigateOnCreate = false,
  onSubmit,
  data: curationData,
}) => {
  // Normalize the curation FormType key onto the TrackerType this render
  // actually belongs to — for 'Vital Sign' that depends on the emitted code.
  const type: TrackerType = normalizeTrackerType(rawType, curationData?.code);
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { formatDateLabel } = useFormat();
  const [componentIndex, setComponentIndex] = useState(0);
  const { backRoute } = HEALTH_TRACKER_MAP[type];

  const {
    state: { activeProfile, isCurator },
  } = useSession();
  const [patient_id] = [activeProfile?.patient_id];
  const {
    state: { isCreating, isEditing },
    finishAddingRecord,
    stopEditingRecord,
  } = useCuration();

  const { data: vitalSignData, loading: vitalSignLoading } = useGetVitalSign({
    record_id: id,
    skip: type !== TrackerType.VitalSigns,
  });
  const { data: bodyData, loading: bodyObservationLoading } =
    useGetBodyObservation({
      record_id: id,
      skip: type !== TrackerType.Body,
    });
  const data =
    curationData ??
    (type === 'body'
      ? bodyData
      : type === TrackerType.Fitness
        ? null
        : vitalSignData);
  /**
   * The one discriminator for the whole component. `typeof data.x === 'function'`
   * is not a safe substitute: ResourceFormWrapper attaches look-alike stubs
   * (getModelInput, getId, ...) to plain curation records, so the method exists
   * but does not behave like the hydrated record's.
   */
  const isHydratedRecord = data instanceof VitalSignObservationRecord;
  const code = data?.code;
  const vitallCode = VitalSignObservationRecord.getVitallCode(code ?? '');
  const isSupportedCode = isSupportedObservationCode(code);

  useEffect(() => {
    let unitOptions: string[] = [];
    if (!code) {
      return;
    }
    // An unknown/noncanonical OCR code has no entry to read unit options from.
    // The map is typed Record<string, …>, so TypeScript believes every index
    // resolves — hence an explicit membership check rather than a truthiness
    // test on the result.
    if (!(vitallCode in ALL_OBSERVATION_OPTIONS_MAP)) {
      return;
    }
    const info = ALL_OBSERVATION_OPTIONS_MAP[vitallCode];
    if (info.units) {
      unitOptions = info.units;
    }
    if (info.components) {
      const totalUnits = info.components.reduce<string[]>((acc, curr) => {
        const [{ units }] = curr;
        const [unit] = units;
        acc.push(unit);
        return acc;
      }, []);
      unitOptions = totalUnits;
    }
    const index = unitOptions.findIndex((unit) => unit === data.unit);
    if (index > -1) {
      setComponentIndex(index);
    }
  }, [code, data?.unit, vitallCode]);

  const [createVitalSign, createVitalSignLoading] = useCreateVitalSign();
  const [updateVitalSign, updateVitalSignLoading] = useUpdateVitalSign();
  const [createBodyObservation, createBodyObservationLoading] =
    useCreateBodyObservation();
  const [updateBodyObservation, updateBodyObservationLoading] =
    useUpdateBodyObservation();
  const [createFitnessObservation, createFitnessObservationLoading] =
    useCreateFitnessObservation();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();

  const { palette } = useTheme();

  const loading =
    vitalSignLoading ||
    updateVitalSignLoading ||
    createVitalSignLoading ||
    bodyObservationLoading ||
    createBodyObservationLoading ||
    updateBodyObservationLoading ||
    createFitnessObservationLoading ||
    recordStatusAndSourceLoading;

  // A hydrated FHIR record exposes getComponents(); auto-curation's plain
  // record carries its OCR'd measurement rows on `components` instead. An
  // unsupported code short-circuits both: the rows would be discarded below,
  // and getComponents() indexes ALL_OBSERVATION_OPTIONS_MAP on that same code.
  let seedComponents: ReducedObservationComponent[] = [];
  if (isSupportedCode) {
    seedComponents = isHydratedRecord
      ? data.getComponents(componentIndex)
      : (data?.components ?? []);
  }

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    // Body/Fitness: default to non-clinical
    // Vital sign: default to clinical
    clinicalStatus:
      data?.clinicalStatus ??
      (type === 'body' || type === TrackerType.Fitness ? false : true),
    code: code ?? '',
    source: data?.source ?? '',
    testDate: data?.testDate ?? '',
    // An unsupported code has no measurement template, so the form renders with
    // no rows and the curator picks a real Type — rather than throwing.
    components:
      code && isSupportedCode
        ? VitalSignObservationRecord.getMeasurementArray(
            code,
            [...seedComponents],
            componentIndex
          )
        : [],
    selfReported: data?.selfReported ?? false,
  };

  const vitalSignValidationSchema: Schema<
    Partial<Omit<VitalSignValues, 'title'>>
  > = Yup.object().shape({
    clinicalStatus: Yup.boolean().required(formatMessage(messages.required)),
    code: Yup.string()
      .required(formatMessage(messages.required))
      // Only the create path builds the Observation through
      // getVitalSignTemplate, which indexes ALL_OBSERVATION_OPTIONS_MAP
      // unguarded. Submitting an unsupported OCR code there throws inside the
      // try, so the curator gets a toast while `finally` still clears the form
      // — the record is lost. Fail validation instead. A hydrated record
      // updates through getModelInput and keeps its existing coding, so it is
      // exempt (its `code` is the comma-joined coding list, not an option key).
      .test(
        'supported-observation-code',
        formatMessage(messages.required),
        (value) => (isHydratedRecord ? true : isSupportedObservationCode(value))
      ),
    source: Yup.string(),
    testDate: Yup.string(),
    components: Yup.array().of(
      Yup.object().shape({
        code: Yup.string().required(formatMessage(messages.required)),
        unit: Yup.string().when('code', {
          is: (code: string) =>
            [
              'pain',
              'body-mass-index',
              'body-waist-height',
              'body-waist-hip',
            ].includes(code),
          then: () => Yup.string().nullable(),
          otherwise: () =>
            Yup.string().required(formatMessage(messages.required)),
        }),
        display: Yup.string().required(formatMessage(messages.required)),
        units: Yup.array()
          .of(
            Yup.object()
              .shape({
                k: Yup.string().required(),
                v: Yup.string().required(),
              })
              .required()
          )
          .nullable()
          .required(),
        value: Yup.number()
          .typeError(formatMessage(messages.number))
          .min(0)
          .required(formatMessage(messages.required)),
        isSingleUnit: Yup.boolean(),
      })
    ),
    selfReported: Yup.boolean().required(formatMessage(messages.required)),
  });
  const validationSchema: Schema<
    Partial<FormValues>,
    any,
    any,
    Flags
  > = vitalSignValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    // TODO: Location
    if (!patient_id) return;

    values = stripEmptyStrings(values);
    await validateForm(stripEmptyStrings(values));

    let obsMetric: VitalSignObservationRecord | null | undefined;
    try {
      if (type === TrackerType.VitalSigns) {
        if (data?.id) {
          const { data: updatedObs } = await updateVitalSign({
            input: {
              id: data.id,
              // Only a hydrated record can turn form values into an
              // ObservationModelInput. ResourceFormWrapper's getModelInput stub
              // echoes FormValues straight back, so it must never be used here.
              observation: isHydratedRecord
                ? data.getModelInput(values)
                : getVitalSignTemplate(patient_id, values, 'vital-signs'),
            },
          });
          obsMetric = updatedObs;
        } else {
          const { data } = await createVitalSign({
            input: {
              ...getVitalSignTemplate(patient_id, values, 'vital-signs'),
            },
          });
          obsMetric = data;
          // Existing behavior preserved by default.
          // In auto-curation flow, disableNavigateOnCreate=true will skip this.
          if (data && !disableNavigateOnCreate) {
            navigate(`${VITAL_SIGNS}${data.vitallCode}/${data.id}`);
          }
        }
      }
      if (type === 'body') {
        const observation = getVitalSignTemplate(
          patient_id,
          {
            ...values,
            source: undefined,
          },
          'body'
        );

        if (data?.id) {
          const { data: updatedObs } = await updateBodyObservation({
            input: {
              id: data.id,
              observation,
            },
          });
          obsMetric = updatedObs;
        } else {
          const { data } = await createBodyObservation({
            input: {
              ...observation,
            },
          });
          obsMetric = data;
          // Existing behavior preserved by default.
          // In auto-curation flow, disableNavigateOnCreate=true will skip this.
          if (data && !disableNavigateOnCreate) {
            navigate(`${BODY}${data.vitallCode}/${data.id}`);
          }
        }
      }
      if (type === TrackerType.Fitness) {
        const observation = getVitalSignTemplate(
          patient_id,
          {
            ...values,
            source: undefined,
          },
          'fitness'
        );
        const { data } = await createFitnessObservation({
          input: {
            ...observation,
          },
        });
        obsMetric = data;
        if (data && !disableNavigateOnCreate) {
          navigate(FITNESS);
        }
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: obsMetric?.getResourceId(),
        // No file attachments for vital sign/body metrics
        files: [],
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      stopEditingRecord();
      finishAddingRecord();
      onSubmit?.();
    }
  }

  if (type === 'sleep') {
    return null;
  }

  if (!vitalSignLoading && !bodyObservationLoading && !data && !isCreating) {
    return (
      <DrilldownWrapper backRoute={backRoute} resource={null}>
        <ErrorBoundaryFallback
          error={{
            code: '404',
            message: formatMessage(messages.notFound),
          }}
          resetErrorBoundary={() => navigate(backRoute)}
        />
      </DrilldownWrapper>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const { code } = values;
        const MAP =
          type === 'body'
            ? BODY_OBSERVATION_OPTIONS_MAP
            : type === TrackerType.Fitness
              ? MANUAL_ENTRY_FITNESS_OPTIONS_MAP
              : VITAL_SIGN_OPTIONS_MAP;
        const OPTIONS =
          type === 'body'
            ? BODY_OBSERVATION_CODE_OPTIONS
            : type === TrackerType.Fitness
              ? MANUAL_ENTRY_FITNESS_CODE_OPTIONS
              : VITAL_SIGN_CODE_OPTIONS;
        const config = code in MAP ? MAP[code] : undefined;

        return (
          <Form>
            <DrilldownWrapper
              associatedRecords={data?.associatedRecords}
              backRoute={isCreating ? backRoute : `${backRoute}${code}`}
              items={
                type === TrackerType.VitalSigns
                  ? [
                      {
                        editingComponent: (
                          <FormikTextField
                            name="source"
                            placeholder={formatMessage(messages.practitioner)}
                          />
                        ),
                        title: data?.source,
                        iconName: 'user-doctor',
                      },
                    ]
                  : undefined
              }
              loading={loading}
              notes={data?.recordNotes}
              resource={data as FhirResource | null}
              selfReported={data?.selfReported}
              subtitle={
                isCreating || isEditing ? (
                  <FormikFhirDateField
                    formats={['datetime', 'date']}
                    name="testDate"
                    onChange={(value) => {
                      setFieldValue('testDate', value);
                    }}
                    value={data?.testDate}
                    max={true}
                  />
                ) : (
                  formatDateLabel(
                    messages.testDate,
                    isHydratedRecord
                      ? data.getTestDateStr(true)
                      : data?.testDate
                  )
                )
              }
              title={`${formatMessage(
                // eslint-disable-next-line no-nested-ternary
                type === 'body'
                  ? messages.body
                  : type === TrackerType.Fitness
                    ? messages.dailyStats
                    : messages.vitalSign
              )}${
                // eslint-disable-next-line no-nested-ternary
                isCreating || (isEditing && values.code)
                  ? `: ${config?.display ?? '-'}`
                  : data?.code
                    ? `: ${config?.display ?? '-'}`
                    : ''
              }`}
            >
              <ColumnLayout>
                <DrilldownItem
                  label={formatMessage(messages.type)}
                  name="code"
                  onChange={(value) => {
                    setFieldValue('code', value);
                    const componentRows =
                      VitalSignObservationRecord.getMeasurementArray(
                        value as string,
                        [],
                        componentIndex
                      );
                    setFieldValue('components', componentRows);
                    setComponentIndex(0);
                  }}
                  options={OPTIONS}
                  type="dropdown"
                  value={data?.displayName}
                />
              </ColumnLayout>

              <DrilldownItem
                headerLabel={formatMessage(messages.clinicalStatus)}
                label={
                  isCreating || isEditing
                    ? formatMessage(
                        values.clinicalStatus
                          ? messages.clinical
                          : messages.nonClinical
                      )
                    : formatMessage(messages.clinicalStatus)
                }
                name="clinicalStatus"
                onChange={(value) => {
                  // Changing clinical status to true should make self reported false
                  if (value) {
                    setFieldValue('selfReported', false);
                  }
                }}
                type="checkbox"
                value={formatMessage(
                  data?.clinicalStatus
                    ? messages.clinical
                    : messages.nonClinical
                )}
              />

              <ColumnLayout>
                <FieldArray
                  name="components"
                  render={() => {
                    return values.components.map((component, index) => {
                      const hasUnits = component.units?.length;
                      const singleUnit = component.units?.length === 1;
                      return (
                        <React.Fragment key={`observation-component-${index}`}>
                          <DrilldownItem
                            label={component.display}
                            name={`components.${index}.value`}
                            type="number"
                            value={component.value}
                          />
                          {hasUnits ? (
                            <DrilldownItem
                              label={formatMessage(messages.unit)}
                              name={`components.${index}.unit`}
                              options={component.units?.filter(
                                (obj): obj is { k: string; v: string } =>
                                  Boolean(obj.k) && Boolean(obj.v)
                              )}
                              readOnly={singleUnit}
                              type="dropdown"
                              value={component.unit}
                            />
                          ) : null}
                        </React.Fragment>
                      );
                    });
                  }}
                />
                {values.code &&
                  config?.components?.length &&
                  (isCreating || isEditing) && (
                    <Dropdown
                      color={palette.primary.main}
                      label="Units"
                      onChange={(value) => {
                        const newComponentIndex = parseInt(value, 10);
                        const componentRows =
                          VitalSignObservationRecord.getMeasurementArray(
                            values.code,
                            [],
                            newComponentIndex
                          );
                        setFieldValue('components', componentRows);
                        setComponentIndex(newComponentIndex);
                      }}
                      value={componentIndex}
                    >
                      {VitalSignObservationRecord.getComponentUnitOptions(
                        values.code
                      ).map((unit, index) => (
                        <option key={index} value={index}>
                          {unit}
                        </option>
                      ))}
                    </Dropdown>
                  )}
              </ColumnLayout>

              <HorizontalRule />
              <RecordSubmitButton />
            </DrilldownWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};

export default withErrorBoundary(VitalSignFormDrilldown, {
  FallbackComponent: ErrorBoundaryFallback,
});
